import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { IconButton } from '../Inputs/IconButton';
import { classNames } from '../Utils/classNames';
import { TextStyle } from './TextStyle';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  ctaSection?: ReactNode;
  className?: string;
  ctaSectionClassName?: string;
};

const messages = defineMessages({
  close: {
    defaultMessage: 'Close',
    id: 'rbrahO',
  },
});

export function Modal(props: ModalProps) {
  const { formatMessage } = useIntl();

  return (
    <Transition.Root show={props.isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={props.onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-secondary bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={twMerge(
                  'relative transform rounded-md bg-gray-1 text-left shadow-xl-down transition-all sm:my-8 sm:w-full sm:max-w-lg',
                  props.className
                )}
              >
                <header className="h-fit w-full px-6 pt-6">
                  <div className="absolute right-0 top-0 pr-6 pt-6">
                    <IconButton
                      onClick={props.onClose}
                      variation="tertiaryLite"
                      size="xSmall"
                      data-testid="modalCloseButton"
                    >
                      <span className="sr-only">
                        {formatMessage(messages.close)}
                      </span>
                      <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </IconButton>
                  </div>
                  <Dialog.Title as="h3" className="leading-6">
                    <TextStyle variant="lg-medium" className="pr-8 text-gray-9">
                      {props.title}
                    </TextStyle>
                  </Dialog.Title>
                </header>
                <div
                  className={classNames('px-6 pt-1', {
                    'py-6': !props.ctaSection,
                    'pt-2': props.ctaSection,
                  })}
                >
                  <div className="text-gray-9">{props.children}</div>
                </div>
                {props.ctaSection && (
                  <div className={twMerge('p-6', props.ctaSectionClassName)}>
                    {props.ctaSection}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
